/**
 * Configuration manager for B2M Tracker
 */
export default class Config {
  constructor() {
    // Default configuration
    this.apiUrl = 'https://tracker.b2metric.com/tracker/tracker/b2m/collect'; // Default API endpoint
    this.experimentApiUrl = 'https://experiments-api.b2metric.com/Experiments/assign'; // A/B test assignment endpoint
    this.experimentCacheTtl = 60 * 1000; // 60 s — upper bound on how late a platform decision (winner, stop) reaches a returning visitor; 0 = always ask
    this.experimentNegativeCacheTtl = 60 * 1000; // 60 s — how long "not assigned" (targeting mismatch) is remembered; never longer than experimentCacheTtl
    this.experimentTimeout = 1500; // ms to wait for the service before serving the last known assignment (only when one exists)
    // Which identity A/B assignment keys on:
    //   'user'        (default) — login id from identify()/setUserId() when
    //                  present, device id otherwise. Same account = same group
    //                  across devices, browsers and private windows.
    //   'fingerprint' — device id always; assignments never follow a login.
    // Switching re-buckets logged-in users once — change only between experiments.
    this.experimentIdentitySource = 'user';

    // Visual experiments (no-code variants applied to the DOM by the SDK).
    //   visualExperiments   undefined (default) → changesets come from
    //                        experiments-api; an array → inline changesets
    //                        (the "export snippet" path, wins over remote);
    //                        false → kill switch (data-visual-experiments="false")
    //   visualExperimentIds experiments whose visual configuration the SDK reads
    //                        from experiments-api (array or comma list;
    //                        data-visual-experiment-ids="id1,id2")
    //   visualConfigUrl     snapshot URL with {experimentId}, or the experiments-api
    //                        base URL; null → the origin of experimentApiUrl
    //   visualEditorOrigins panel origins allowed to open the visual editor on
    //                        this site (window.opener handshake); extend via
    //                        data-visual-editor-origins="https://a,https://b"
    //   visualEditorUrl     override for the editor bundle URL (defaults to
    //                        b2m-visual-editor.js next to the SDK bundle)
    this.visualExperiments = undefined;
    this.visualExperimentIds = [];
    this.visualConfigUrl = null;
    this.visualEditorOrigins = ['https://analytics.b2metric.com', 'https://analytics-test.b2metric.com', 'https://iqbeta.b2metric.com', 'https://iqbeta-test.b2metric.com'];
    this.visualEditorUrl = null;
    this.apiKey = null;

    // Identity source. Rollout is staged (see docs/identity-source-deviceclass.md):
    //   'deviceClass' (default) — STAGE 1: the deterministic device-class hash
    //                  IS the identity. The same physical device yields the
    //                  same id in every browser and in private windows and
    //                  after a full site-data wipe. Identical devices collide
    //                  until the resolve service (stage 2) disambiguates them.
    //   'salt'        — rollback switch: random per-install salt validated
    //                  against the stored device-signal vector (separates
    //                  identical devices, scoped to one browser profile).
    // Switching modes re-mints the identity once; the old→new bridge is
    // declared via the existing identity_reset event.
    this.identitySource = 'deviceClass';
    // STAGE 2 — set this once the backend endpoint ships; nothing else to do:
    //   POST { fingerprint_id, proposed_id } → { user_id }
    // The service tells identical devices apart server-side (e.g. by source
    // IP) and returns the canonical id for this device; the SDK persists it.
    // Browsers that already carry a locally-derived hash identity upgrade
    // automatically with ONE round-trip on their next visit (bridged via
    // identity_reset when the id changes). Absent/slow/failing endpoint →
    // fall back to the local hash, never block.
    this.identityResolveUrl = null;
    this.identityResolveTimeoutMs = 800;

    // Session Replay configuration
    this.enableSessionReplay = false;        // opt-in — disabled by default
    this.sessionReplayScriptUrl = 'https://cdn.jsdelivr.net/gh/B2M-Team/session-replay-script-dist/script.js';
    this.sessionReplayHost = 'https://sessionreplay-api.b2metric.com'; // test: https://sessionreplay-api-test.b2metric.com
    this.sessionReplaySiteId = null;         // data-site-id value — numeric (must be provided)
    this.sessionReplayUseCustomSessionId = false; // when true, forward data-use-custom-session-id="true"
                                                  // and start SR with the Web SDK's own session ID
    this.debug = false;
    this.batchSize = 5;
    this.sendInterval = 30000; // 30 seconds default
    this.respectDoNotTrack = false; // Disabled by default (GA also ignores DNT)
    this.targetPlatforms = ["google", "meta", "tiktok", "b2m"];
    this.retryCount = 0;
    this.maxRetries = 3;
    this.retryTimeout = 30000;
    
    // Session configuration
    this.sessionTimeout = 30 * 60 * 1000;        // 30 min inactivity timeout
    this.maxSessionDuration = 24 * 60 * 60 * 1000; // 24 hr hard cap (PostHog best practice)
    
    // Bot filtering - filter out known bots and crawlers
    this.filterBots = true; // Enabled by default (GTM doesn't do this, giving us cleaner data)
    
    // Consent management (GDPR/KVKK)
    // When true, events are CAPTURED (queued) but NOT transmitted — not even on
    // page unload — until giveConsent() is called, which then flushes them.
    // revokeConsent() drops the captured-but-unsent queue. This is consent-aware
    // capture, NOT a consent bypass: the integrator (via their CMP) decides when
    // to call giveConsent(). Note attribution is still written to storage for
    // capture; classify those cookies in your CMP accordingly (or load the SDK
    // only after consent if your cookies aren't essential).
    // Script tag: data-require-consent="true"
    this.requireConsent = false; // Disabled by default for backward compatibility
    
    // Duplicate event prevention
    this.duplicatePreventionTimeout = 5000; // 5 seconds default
    
    // SPA duplicate prevention - shorter timeout for navigation events
    this.spaNavigationTimeout = 300; // 300ms for SPA navigations (hızlı geçişleri yakala)
    
    // SPA (Single Page Application) navigation tracking
    this.enableSPATracking = true; // Auto-track route changes in SPA frameworks
    this.spaPollingInterval = 100; // 100ms polling (daha hızlı algılama)
    
    // E-commerce platform detection
    this.enablePlatformDetection = true; // Auto-detect Ticimax, Shopify, WooCommerce, etc.
    this.forcePlatform = null; // Override auto-detection: 'ticimax', 'shopify', 'woocommerce', 'generic'

    // Add-to-cart click context. Platform trackers remember WHERE the last
    // add-to-cart interaction happened (the DOM container the click landed in)
    // and stamp it on add_to_cart as `add_to_cart_source`. Needed because a
    // listing page can add to cart both inline (product card) and from a
    // variant/quick-view popup — same URL, same dataLayer payload — and the
    // dataLayer push arrives after the server confirms, when the popup may
    // already be closed. Ordered map: first matching selector wins, so the
    // popup entry must precede the list entry (a dialog sits outside the card).
    // Values are CSS selector lists; integrations override per site:
    //   addToCartContextSelectors: { popup: '.my-dialog', list: '.my-card' }
    this.addToCartContextSelectors = {
      popup: 'eb-add-to-cart-dialog, .add-to-cart-dialog, [role="dialog"], .quick-view, .quickview',
      recommender: '.ins-add-to-cart-wrapper, .ins-web-smart-recommender-box-item, [data-recommender]',
      product_detail: '.ProductDetailsPageTemplate, .product-detail, .product-page, .pdp, .urun-detay',
      list: 'eb-product-card, .product-item, .product-card, .product-list, .urun-item'
    };
    // Clicks inside these containers never update the context: the
    // "added to cart" success dialog (it is a [role=dialog] but not where the
    // add happened) and consent banners.
    this.addToCartContextIgnoreSelectors = '.add-to-cart-dialog-success, .cookie-banner, .cookie-consent, #onetrust-consent-sdk';
    // How long a remembered click context stays valid (server round-trip +
    // success handling). Older contexts fall back to the dataLayer push's own
    // pageType, then to URL/DOM page-type detection.
    this.addToCartContextTtlMs = 15000;

    // Tracking mode — how events are collected when the SDK is embedded.
    //   native    → current auto-tracking behaviour (default; backward compatible)
    //   datalayer → only listen to window.dataLayer; never self-generate events
    //   hybrid    → dataLayer first, then auto-collect registered events missing from DL
    // Script tag: data-tracking-mode="native|datalayer|hybrid"
    // Undefined / invalid → native (must not break existing integrations)
    this.trackingMode = 'native';

    // GA bridge — mirror an existing Google Analytics / GTM event stream into
    // B2Metric instead of instrumenting the SDK separately.
    //
    // OFF by default and it must stay that way: forwarding GA traffic to a
    // second vendor needs the customer's consent notice to cover B2Metric.
    // Script tag: data-ga-bridge="true" [data-ga-bridge-mode, data-ga-bridge-endpoints]
    //
    //   { enabled, mode: 'auto'|'datalayer'|'gtag'|'network', endpoints[],
    //     nameMap, eventFilter, maxEventsPerPage, requireAnalyticsConsent,
    //     exclusive }   — exclusive: ONLY GA-mirrored events, no native tracking
    //
    // See docs/ga-bridge-feasibility.md for per-adapter coverage.
    this.gaBridge = { enabled: false };
    
    // Critical events that should be sent immediately (not batched).
    // ab_test_view is included so exposure reaches the backend even when the
    // visitor bounces before the batch/interval flush (A/B attribution must
    // not depend on queue timing). user_identify is immediate so a property
    // change reaches the central user table without waiting for the batch flush.
    this.immediateEvents = ['page_view', 'session_start', 'session_end', 'purchase', 'ab_test_view', 'user_identify'];
    
    // iframe Mode Configuration
    // When SDK runs inside a 3rd-party iframe (e.g., Pulpoar widget embedded on other sites)
    this.iframeMode = false;          // Auto-detected or manually set
    this.iframeAutoDetect = true;     // Auto-detect if running inside an iframe

    // Countly Interceptor
    // Pulpoar gibi Countly kullanan widget'larda Countly.add_event / Countly.q.push çağrılarını
    // otomatik yakala ve B2M'e yönlendir. Pulpoar kodunu değiştirmeden geçişi sağlar.
    this.enableCountlyInterceptor = false;

    // Custom Event Library
    // Belirli bir 3rd-party widget/SDK'nın eventlerini otomatik toplamak için kullanılır.
    // Desteklenen değerler: 'pulpo'
    // Script tag'den okunur: data-custom-event-lib="pulpo"
    this.customEventLib = null;

    // App Identifier (manual name override)
    // user_params.app_identifier — hangi web projesi/uygulamadan geldiğini
    // ayırt etmek için. Aynı origin altında birden fazla site/subpath olduğunda
    // hostname yetmez; entegrasyonda serbest isim verilir:
    //   data-app-identifier="checkout-app"  veya  b2mInit({ appIdentifier: 'checkout-app' })
    // null / boş / sadece whitespace → mevcut davranış: window.location.hostname
    // (iframe'de host page domain). Mevcut entegrasyonlar dokunulmadan kalır.
    this.appIdentifier = null;

    // Cookie Domain (cross-subdomain ATTRIBUTION sharing)
    // Shares first-touch UTM, campaign params and referrer across subdomains
    // — so a user who lands on birfatura.com and then registers on
    // uygulama.birfatura.com keeps the same utm_source instead of starting
    // fresh. Identity/session are NOT shared (user continuity comes from the
    // device fingerprint, which resolves identically on every subdomain).
    //   'auto' (default) → auto-derive the registrable domain (e.g. ".birfatura.com")
    //   '.example.com'   → use this domain explicitly
    //   false/'off'/null → disable (host-only storage, no cross-subdomain sharing)
    // Script tag: data-cookie-domain="auto" | ".example.com" | "off"
    this.cookieDomain = 'auto';

    // A/B-only mode. When true, the SDK runs purely as an experiment-exposure
    // client: it stops sending behavioural events (page_view, session_start/end,
    // clicks, form_submit and the engagement group: scroll_depth, time_on_page…)
    // but KEEPS sending ab_test_view and user properties. This lets an A/B tool
    // still receive exposure signals while behavioural data already pulled from
    // BigQuery is not duplicated. Defaults to false for backward compatibility —
    // normal integrations keep collecting both core events and ab_test_view.
    this.abMode = false;

    // Property type schema for dtype consistency. Declared types are enforced on
    // every event_params value (coerced to the declared type; non-coercible values
    // fall back to string). Empty {} = no enforcement (backward compatible).
    // Allowed values: 'number' | 'string' | 'boolean' | 'array'
    // Script tag: data-property-types='{"purchase_count":"number"}'
    this.propertyTypes = {};

    // Automatic dtype stabilization for UNDECLARED keys (ANALYTICS-6897).
    // Sites feeding dataLayer send the same property as boolean on one page
    // and number on another (active_filter_count), or float vs "12.50" string
    // (amount); each type became a duplicate Event Properties row. The SDK
    // remembers the shapes seen per key (localStorage) and converts every
    // value to one order-independent type at send time. Keys named in
    // propertyTypes are untouched (declared wins); keys that always arrive
    // with one type are never converted.
    // Script tag: data-auto-property-types="false" to opt out.
    this.autoPropertyTypes = true;

    // Event Groups Configuration
    this.eventGroups = this.getDefaultEventGroups();
  }
  
  getDefaultEventGroups() {
    return {
      // Core events (behavioural — turned off in A/B-only mode via abMode)
      core: {
        enabled: true,
        events: ['page_view', 'session_start', 'session_end', 'button_click', 'link_click', 'form_submit'],
        autoTrack: ['page_view', 'session_start', 'button_click', 'link_click', 'form_submit']
      },

      // Experiments — decoupled from core so A/B exposure (ab_test_view) keeps
      // firing even when behavioural core events are disabled (abMode).
      // Always enabled; not affected by abMode.
      experiments: {
        enabled: true,
        // ve_change_* are the visual runtime's health telemetry (selector
        // missing / re-apply storm), fired at most once per change per page.
        events: ['ab_test_view', 've_change_missing', 've_change_conflict'],
        autoTrack: []
      },

      // Identity — carries the current user_property snapshot to the central
      // user table. Decoupled from core (like experiments) so user_identify
      // keeps firing at session start and on every property change even when
      // behavioural core events are disabled (abMode). Always enabled.
      identity: {
        enabled: true,
        events: ['user_identify'],
        autoTrack: []
      },

      // E-commerce events
      ecommerce: {
        enabled: false,
        events: [
          'view_item', 'view_item_list', 'select_item', 
          'add_to_cart', 'remove_from_cart', 'view_cart',
          'begin_checkout', 'add_shipping_info', 'add_payment_info',
          'purchase', 'refund',
          'add_to_wishlist', 'remove_from_wishlist',
          'view_promotion', 'select_promotion',
          'coupon_apply'
        ],
        autoTrack: [
          'view_item',           // Ürün detay sayfası otomatik
          'view_item_list',      // Kategori/liste sayfaları otomatik
          'select_item',         // Ürün seçimi otomatik
          'add_to_cart',         // Sepete ekle buton otomatik
          'remove_from_cart',    // Sepetten çıkar buton otomatik
          'view_cart',           // Sepet sayfası otomatik
          'begin_checkout',      // Checkout sayfası otomatik
          'add_shipping_info',   // Kargo bilgisi ekleme otomatik
          'add_payment_info',    // Ödeme bilgisi ekleme otomatik
          'purchase',            // Satın alma otomatik (thank you page)
          'refund',              // İade otomatik
          'add_to_wishlist',     // Favorilere ekleme otomatik
          'remove_from_wishlist',// Favorilerden çıkarma otomatik
          'view_promotion',      // Promosyon görüntüleme otomatik
          'select_promotion',    // Promosyon seçimi otomatik
          'coupon_apply'         // Kupon uygulama otomatik
        ]
      },
      
      // Media and content
      media: {
        enabled: false,
        events: ['video_start', 'video_complete', 'video_pause', 'image_zoom', 'article_read', 'pdf_view', 'social_share'],
        autoTrack: ['video_start', 'video_complete', 'video_pause']
      },
      
      // User account operations
      user: {
        enabled: false,
        events: ['user_register', 'user_login', 'user_logout', 'profile_update', 'password_change', 'account_delete'],
        autoTrack: ['user_register', 'user_login', 'user_logout', 'profile_update', 'password_change', 'account_delete']
      },
      
      // Search and navigation
      search: {
        enabled: false,
        events: ['search', 'search_result_click', 'filter_apply', 'sort_change', 'category_view', 'pagination'],
        autoTrack: ['search']
      },
      
      // Performance and technical
      performance: {
        enabled: false,
        events: ['page_performance', 'javascript_error', 'api_timing', 'network_change', 'image_load_error'],
        autoTrack: ['page_performance', 'javascript_error']
      },
      
      // Engagement
      engagement: {
        enabled: true,
        events: ['scroll_depth', 'time_on_page', 'tab_change', 'modal_open', 'accordion_toggle', 'user_idle', 'user_active'],
        autoTrack: ['scroll_depth', 'time_on_page', 'tab_change', 'modal_open', 'accordion_toggle', 'user_idle', 'user_active']
      },
      
      // Communication
      communication: {
        enabled: false,
        events: ['chat_start', 'phone_click', 'email_send', 'contact_form', 'faq_view', 'review_submit'],
        autoTrack: ['phone_click', 'contact_form']
      },
      
      // Marketing
      marketing: {
        enabled: false,
        events: ['banner_click', 'newsletter_signup', 'promotion_view', 'popup_view'],
        autoTrack: ['banner_click', 'newsletter_signup', 'popup_view']
      },
      
      // Mobile specific
      mobile: {
        enabled: false,
        events: ['app_download_click', 'qr_code_scan', 'location_used', 'orientation_change', 'battery_low'],
        autoTrack: ['app_download_click', 'orientation_change']
      }
    };
  }
  
  /**
   * Normalize trackingMode. Unknown / empty values fall back to native so
   * existing sites without the attribute keep current behaviour.
   * @param {any} value
   * @returns {'native'|'datalayer'|'hybrid'}
   */
  resolveTrackingMode(value) {
    const mode = (value == null ? 'native' : String(value)).toLowerCase().trim();
    if (mode === 'datalayer' || mode === 'hybrid' || mode === 'native') {
      return mode;
    }
    return 'native';
  }

  /**
   * Normalize appIdentifier. Empty / whitespace → null so senders fall back
   * to hostname (backward-compatible default for sites that never set it).
   * Non-empty values are trimmed free-form names (not required to look like a domain).
   * @param {any} value
   * @returns {string|null}
   */
  resolveAppIdentifier(value) {
    if (value == null) return null;
    const trimmed = String(value).trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  isNativeMode() {
    return this.trackingMode === 'native';
  }

  isDataLayerMode() {
    return this.trackingMode === 'datalayer';
  }

  isHybridMode() {
    return this.trackingMode === 'hybrid';
  }

  /**
   * Exclusive modes replace native auto-collection with dataLayer-first behaviour.
   */
  isExclusiveDataLayerCollection() {
    return this.isDataLayerMode() || this.isHybridMode();
  }

  /**
   * Whether the dataLayer push listener should be installed.
   * Only datalayer / hybrid — native never listens to dataLayer.
   */
  shouldUseDataLayerListener() {
    return this.isExclusiveDataLayerCollection();
  }

  init(userConfig) {
    // Merge user configuration with defaults
    Object.assign(this, userConfig);

    // Legacy flags removed from the product surface. If an old embed still passes
    // them (script attr / JS config), accept silently — no throw, no behaviour.
    delete this.enableGTMDataLayerListener;
    delete this.gtmExcludeEvents;

    // Resolve trackingMode after merge so script/manual config are normalized.
    // Invalid values → native (safe default for production integrations).
    this.trackingMode = this.resolveTrackingMode(this.trackingMode);

    // Only an explicit false / "false" opts out of dtype stabilization.
    this.autoPropertyTypes = !(this.autoPropertyTypes === false ||
      String(this.autoPropertyTypes).toLowerCase() === 'false');

    // Resolve appIdentifier after merge. Empty/whitespace → null → hostname
    // fallback at send time. Explicit non-empty name wins (manual override).
    this.appIdentifier = this.resolveAppIdentifier(this.appIdentifier);

    // Normalize identitySource — only 'salt' is a valid opt-out; anything
    // else (including typos) resolves to the 'deviceClass' default so a typo
    // can never silently flip every visitor's identity model.
    if (this.identitySource !== 'salt') {
      this.identitySource = 'deviceClass';
    }

    // Normalize experimentIdentitySource — unknown values fall back to 'user'.
    if (this.experimentIdentitySource !== 'fingerprint') {
      this.experimentIdentitySource = 'user';
    }
    
    // Auto-detect iframe mode if enabled
    if (this.iframeAutoDetect && !userConfig.hasOwnProperty('iframeMode')) {
      try {
        this.iframeMode = window.self !== window.top;
      } catch (e) {
        // Cross-origin: accessing window.top throws SecurityError → we are in an iframe
        this.iframeMode = true;
      }
    }
    
    // Apply iframe-optimized defaults when in iframe mode
    if (this.iframeMode) {
      // Use smaller batch size (iframe can close anytime)
      if (!userConfig.hasOwnProperty('batchSize')) {
        this.batchSize = 1;
      }
      // Shorter send interval
      if (!userConfig.hasOwnProperty('sendInterval')) {
        this.sendInterval = 5000;
      }
      // Disable SPA tracking (not needed in iframe widgets)
      if (!userConfig.hasOwnProperty('enableSPATracking')) {
        this.enableSPATracking = false;
      }
      // Disable platform detection (iframe doesn't have access to parent DOM)
      if (!userConfig.hasOwnProperty('enablePlatformDetection')) {
        this.enablePlatformDetection = false;
      }
      
      if (this.debug) {
        console.log('[B2M SDK] iframe mode detected - using optimized settings:', {
          batchSize: this.batchSize,
          sendInterval: this.sendInterval,
          enableSPATracking: this.enableSPATracking,
          enablePlatformDetection: this.enablePlatformDetection
        });
      }
    }
    
    // Merge event groups if provided.
    // NOTE: Object.assign above has already REPLACED this.eventGroups with the
    // user's map, so merging against this.eventGroups would silently drop every
    // default group the integrator didn't list (data-event-groups="core,…"
    // wiped the experiments group and with it ab_test_view). Merge against the
    // pristine defaults instead so unlisted groups keep their default state.
    if (userConfig.eventGroups) {
      if (this.debug) console.log('Before merge:', JSON.stringify(this.eventGroups, null, 2));
      this.eventGroups = this.mergeEventGroups(this.getDefaultEventGroups(), userConfig.eventGroups);
      if (this.debug) console.log('After merge:', JSON.stringify(this.eventGroups, null, 2));
    }

    // Validate event groups structure
    this.validateEventGroups();
    if (this.debug) console.log('After validation:', JSON.stringify(this.eventGroups, null, 2));

    // addToCartContextSelectors: a partial per-site map (e.g. only `popup`)
    // must not wipe the other defaults. User entries come first — order is
    // match priority — then any default key the user did not mention.
    if (userConfig.addToCartContextSelectors && typeof userConfig.addToCartContextSelectors === 'object') {
      const defaults = new Config().addToCartContextSelectors;
      const merged = { ...userConfig.addToCartContextSelectors };
      Object.keys(defaults).forEach((k) => { if (!(k in merged)) merged[k] = defaults[k]; });
      this.addToCartContextSelectors = merged;
    }

    // The experiments group must always be present and enabled: A/B exposure
    // (ab_test_view) is core to attribution and must not depend on which
    // groups an integration happens to list. abMode below never touches it.
    if (!this.eventGroups.experiments || !this.eventGroups.experiments.enabled) {
      this.eventGroups.experiments = this.getDefaultEventGroups().experiments;
      if (this.debug) console.log('[B2M SDK] experiments group restored (ab_test_view always active)');
    }

    // The identity group must always be present and enabled: user_identify
    // feeds the central user table (all-time user properties) and must fire
    // at session start / on property changes regardless of which groups an
    // integration lists. abMode below never touches it.
    if (!this.eventGroups.identity || !this.eventGroups.identity.enabled) {
      this.eventGroups.identity = this.getDefaultEventGroups().identity;
      if (this.debug) console.log('[B2M SDK] identity group restored (user_identify always active)');
    }

    // Apply A/B-only mode AFTER validation so it can't be reverted by the
    // default-restore logic in validateEventGroups(). Turns off behavioural data
    // (core + engagement) while leaving the experiments group active, so
    // ab_test_view and user properties keep flowing.
    if (this.abMode === true) {
      if (this.eventGroups.core)       this.eventGroups.core.enabled = false;
      if (this.eventGroups.engagement) this.eventGroups.engagement.enabled = false;
      if (this.debug) console.log('[B2M SDK] abMode=true → core & engagement disabled; experiments (ab_test_view) still active');
    }

    // GA-bridge-exclusive mode: the site's own Google Analytics stream is the
    // ONLY behavioural source ("just mirror our GA"). Native auto-tracking
    // (core + engagement) and the SDK-internal user_identify are switched off.
    // Bridged events bypass group gating and still carry user_params, so the
    // central user table keeps being fed. Ignored unless gaBridge.enabled.
    if (this.isGaBridgeExclusive()) {
      if (this.eventGroups.core)       this.eventGroups.core.enabled = false;
      if (this.eventGroups.engagement) this.eventGroups.engagement.enabled = false;
      if (this.eventGroups.identity)   this.eventGroups.identity.enabled = false;
      if (this.debug) console.log('[B2M SDK] gaBridge.exclusive=true → only GA-mirrored events are sent');
    }
    
    // Set default customApiUrl if not provided but apiUrl exists
    if (!this.customApiUrl && this.apiUrl) {
      this.customApiUrl = 'https://tracker.b2metric.com/tracker/tracker/b2m/custom';
    }
    
    return this;
  }
  
  validateEventGroups() {
    if (!this.eventGroups || typeof this.eventGroups !== 'object') {
      console.warn('Invalid eventGroups configuration, using defaults');
      this.eventGroups = this.getDefaultEventGroups();
      return;
    }
    
    const defaultGroups = this.getDefaultEventGroups();
    if (this.debug) console.log('Default groups for validation:', JSON.stringify(defaultGroups, null, 2));
    
    // Ensure each group has required properties
    Object.keys(this.eventGroups).forEach(groupName => {
      const group = this.eventGroups[groupName];
      if (this.debug) console.log(`Validating group '${groupName}':`, JSON.stringify(group, null, 2));
      
      if (!group || typeof group !== 'object') {
        console.warn(`Invalid event group '${groupName}', resetting to default`);
        this.eventGroups[groupName] = defaultGroups[groupName] || {
          enabled: false,
          events: [],
          autoTrack: []
        };
        return;
      }
      
      // Ensure required properties exist
      if (typeof group.enabled !== 'boolean') {
        group.enabled = false;
      }
      
      if (!Array.isArray(group.events)) {
        if (this.debug) console.log(`Group '${groupName}' events is not array, fixing...`);
        // If group exists in defaults, use default events
        if (defaultGroups[groupName] && Array.isArray(defaultGroups[groupName].events)) {
          group.events = [...defaultGroups[groupName].events];
          if (this.debug) console.log(`Set default events for '${groupName}':`, group.events);
        } else {
          group.events = [];
        }
      } else if (group.events.length === 0 && defaultGroups[groupName] && defaultGroups[groupName].events.length > 0) {
        // If events array is empty but defaults exist, restore defaults
        group.events = [...defaultGroups[groupName].events];
        if (this.debug) console.log(`Restored default events for '${groupName}':`, group.events);
      }
      
      if (!Array.isArray(group.autoTrack)) {
        if (this.debug) console.log(`Group '${groupName}' autoTrack is not array, fixing...`);
        // If group exists in defaults, use default autoTrack
        if (defaultGroups[groupName] && Array.isArray(defaultGroups[groupName].autoTrack)) {
          group.autoTrack = [...defaultGroups[groupName].autoTrack];
          if (this.debug) console.log(`Set default autoTrack for '${groupName}':`, group.autoTrack);
        } else {
          group.autoTrack = [];
        }
      } else if (group.autoTrack.length === 0 && defaultGroups[groupName] && defaultGroups[groupName].autoTrack.length > 0) {
        // If autoTrack array is empty but defaults exist, restore defaults
        group.autoTrack = [...defaultGroups[groupName].autoTrack];
        if (this.debug) console.log(`Restored default autoTrack for '${groupName}':`, group.autoTrack);
      }
    });
  }
  
  mergeEventGroups(defaultGroups, userGroups) {
    const merged = { ...defaultGroups };
    
    Object.keys(userGroups).forEach(groupName => {
      if (merged[groupName]) {
        // Store default arrays before merging - with safety checks
        const defaultEvents = Array.isArray(merged[groupName].events) ? [...merged[groupName].events] : [];
        const defaultAutoTrack = Array.isArray(merged[groupName].autoTrack) ? [...merged[groupName].autoTrack] : [];
        
        // Merge existing group
        merged[groupName] = {
          ...merged[groupName],
          ...userGroups[groupName]
        };
        
        // If user didn't provide events/autoTrack arrays, restore defaults
        if (!userGroups[groupName].hasOwnProperty('events')) {
          merged[groupName].events = defaultEvents;
        } else if (Array.isArray(userGroups[groupName].events)) {
          // If user provided events, merge with defaults
          merged[groupName].events = [
            ...new Set([...defaultEvents, ...userGroups[groupName].events])
          ];
        } else {
          // If user provided non-array events, use defaults
          merged[groupName].events = defaultEvents;
        }
        
        if (!userGroups[groupName].hasOwnProperty('autoTrack')) {
          merged[groupName].autoTrack = defaultAutoTrack;
        } else if (Array.isArray(userGroups[groupName].autoTrack)) {
          // If user provided autoTrack, merge with defaults
          merged[groupName].autoTrack = [
            ...new Set([...defaultAutoTrack, ...userGroups[groupName].autoTrack])
          ];
        } else {
          // If user provided non-array autoTrack, use defaults
          merged[groupName].autoTrack = defaultAutoTrack;
        }
      } else {
        // Add new group - make sure it has required properties
        merged[groupName] = {
          enabled: false,
          events: [],
          autoTrack: [],
          ...userGroups[groupName]
        };
        
        // Ensure arrays are actually arrays
        if (!Array.isArray(merged[groupName].events)) {
          merged[groupName].events = [];
        }
        if (!Array.isArray(merged[groupName].autoTrack)) {
          merged[groupName].autoTrack = [];
        }
      }
    });
    
    return merged;
  }
  
  /**
   * True when the GA bridge is on AND configured as the exclusive source.
   * @returns {boolean}
   */
  isGaBridgeExclusive() {
    const g = this.gaBridge;
    return !!(g && typeof g === 'object' && g.enabled === true && g.exclusive === true);
  }

  isEventEnabled(eventName) {
    // Chat widget events are never group-gated: when the chat module is on the
    // page it must be able to report its own interactions (chat_widget_open,
    // chat_message_sent, …) without the integration having to list them in
    // data-event-groups. Only the widget emits chat_-prefixed names.
    if (eventName.startsWith('chat_')) return true;

    // SDK-internal identity event: fired at most once per identity change and
    // carries the old→new id bridge the backend stitches on. Losing it to a
    // restrictive data-event-groups config would silently break stitching.
    if (eventName === 'identity_reset') return true;

    for (const [groupName, group] of Object.entries(this.eventGroups)) {
      if (group.enabled && group.events && Array.isArray(group.events) && group.events.includes(eventName)) {
        return true;
      }
    }

    if (this.debug) console.log(`[B2M SDK] event '${eventName}' not in any enabled group — dropped`);
    return false;
  }
  
  isAutoTrackEnabled(eventName) {
    for (const [groupName, group] of Object.entries(this.eventGroups)) {
      if (group.enabled && group.autoTrack && Array.isArray(group.autoTrack) && group.autoTrack.includes(eventName)) {
        return true;
      }
    }
    return false;
  }
  
  getEnabledEvents() {
    const enabledEvents = [];
    Object.values(this.eventGroups).forEach(group => {
      if (group.enabled && group.events && Array.isArray(group.events)) {
        enabledEvents.push(...group.events);
      }
    });
    return [...new Set(enabledEvents)];
  }
  
  getAutoTrackEvents() {
    const autoTrackEvents = [];
    Object.values(this.eventGroups).forEach(group => {
      if (group.enabled && group.autoTrack && Array.isArray(group.autoTrack)) {
        autoTrackEvents.push(...group.autoTrack);
      }
    });
    return [...new Set(autoTrackEvents)];
  }
  
  getEventGroup(eventName) {
    // Mirror of the chat_ bypass in isEventEnabled — widget events report
    // under a stable 'chat' group even though no configured group lists them.
    if (eventName.startsWith('chat_')) return 'chat';
    // Mirror of the identity_reset bypass in isEventEnabled.
    if (eventName === 'identity_reset') return 'identity';

    for (const [groupName, group] of Object.entries(this.eventGroups)) {
      if (group.events && Array.isArray(group.events) && group.events.includes(eventName)) {
        return groupName;
      }
    }
    return null;
  }
  
  enableEventGroup(groupName) {
    if (this.eventGroups[groupName]) {
      this.eventGroups[groupName].enabled = true;
      return true;
    }
    return false;
  }
  
  disableEventGroup(groupName) {
    if (this.eventGroups[groupName] && groupName !== 'core') {
      this.eventGroups[groupName].enabled = false;
      return true;
    }
    return false;
  }
  
  validate() {
    // Check required fields
    // Accept either 'apiKey' or 'token' (token is an alias)
    if (this.token && !this.apiKey) {
      this.apiKey = this.token;
    }
    
    return (
      this.apiUrl && 
      this.apiKey
    );
  }
  
  getAll() {
    return { ...this };
  }
} 